/**
 * Shared form field rendering components extracted from FormRendererPage.
 * Used by the encounter form renderer, registration form page, and grouped edit pages.
 */
import { Fragment, useState, useMemo, useRef, useEffect, type CSSProperties } from 'react';
import {
  type FormFieldDef,
  type FormFieldsSchemaV1,
  type FormSection,
  type FormTemplatePageRow,
  type FormTemplateMappingRow,
  resolveBindingKey,
  isFieldEditable,
  parseRichText,
  stringifyRichText,
  resolveEditableRichText,
  type RichTextConfig,
  readMappingPresentation,
  layoutRichTextFlow,
  selectFlowAnchor,
  type FlowBlock,
  resolveMappingLabelText,
  type RichTextDoc,
  type FlowFragment,
  PAGE_BOTTOM_MARGIN_PCT,
  A4_ASPECT,
  isFormHeaderMapping,
  readFormHeaderConfig,
} from '@helios/data-access';
import { RichTextEditor, RichTextView } from '../../../components/RichTextField';
import { MappedFieldBox } from '../../../components/MappedFieldBox';
import { FormHeaderBox } from '../../../components/FormHeaderBox';
import { RichTextFlowMeasurer } from '../../../components/RichTextFlowMeasurer';

// ─── Value Resolution ───

/**
 * Resolve the runtime value for a field, respecting binding mode precedence.
 */
export function resolveBoundFieldValue(
  field: FormFieldDef,
  storedValues: Record<string, string>,
  bindingData: Record<string, string>,
): string | undefined {
  if (!field.binding) {
    return storedValues[field.key] ?? undefined;
  }
  const boundKey = resolveBindingKey(field);
  const boundValue = boundKey ? bindingData[boundKey] : undefined;

  if (field.binding.mode === 'read_only') {
    return boundValue ?? undefined;
  }
  const stored = storedValues[field.key];
  if (stored !== undefined && stored !== '') return stored;
  return boundValue ?? undefined;
}

/**
 * Resolve display value for a field instance. Used by template overlay and
 * values-summary fallback views. Returns a display string (never undefined).
 */
export function resolveInstanceValue(
  field: FormFieldDef | undefined,
  values: Record<string, string>,
  signatures: Record<string, string>,
  bindingData: Record<string, string>,
): string {
  if (!field) return '—';
  if (field.type === 'drawing') return field.label || 'Drawing';
  if (field.type === 'signature') return signatures[field.key] ? '(signed)' : '(unsigned)';
  return resolveBoundFieldValue(field, values, bindingData) || '—';
}

// ─── Signature Pad ───

export function SignaturePad({
  fieldKey,
  existingData,
  onSave,
  readOnly,
}: {
  fieldKey: string;
  existingData: string | null;
  onSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const strokesRef = useRef<Array<{ x: number; y: number }[]>>([]);
  const currentStrokeRef = useRef<{ x: number; y: number }[]>([]);

  useEffect(() => {
    if (existingData && canvasRef.current) {
      try {
        const parsed = JSON.parse(existingData);
        if (parsed.strokes && canvasRef.current) {
          strokesRef.current = parsed.strokes;
          redraw();
          setHasContent(true);
        }
      } catch {
        // ignore
      }
    }
  }, [existingData]);

  const getPos = (e: React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas) canvas.setPointerCapture(e.pointerId);
    setDrawing(true);
    currentStrokeRef.current = [getPos(e)];
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawing || readOnly) return;
    e.preventDefault();
    const pos = getPos(e);
    currentStrokeRef.current.push(pos);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pts = currentStrokeRef.current;
    if (pts.length < 2) return;
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[pts.length - 2].x, pts[pts.length - 2].y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    if (currentStrokeRef.current.length > 1) {
      strokesRef.current.push([...currentStrokeRef.current]);
      setHasContent(true);
      const strokeData = JSON.stringify({ version: 1, strokes: strokesRef.current });
      const dataUrl = canvasRef.current?.toDataURL('image/png') ?? '';
      onSave(fieldKey, dataUrl, strokeData);
    }
    currentStrokeRef.current = [];
  };

  const handleClear = () => {
    strokesRef.current = [];
    currentStrokeRef.current = [];
    setHasContent(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
    onSave(fieldKey, '', '{}');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          width: '100%',
          maxWidth: 500,
          height: 150,
          border: readOnly ? 'none' : '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          cursor: readOnly ? 'default' : 'crosshair',
          touchAction: 'none',
          background: readOnly ? 'transparent' : '#fff',
          display: 'block',
        }}
      />
      {!readOnly && hasContent && (
        <button
          onClick={handleClear}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            color: 'var(--color-danger-text, #dc2626)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Clear signature
        </button>
      )}
    </div>
  );
}

// ─── Signature Preview ───

export function SignaturePreview({
  strokeData,
  width,
  height,
}: {
  strokeData: string;
  width: number;
  height: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    try {
      const parsed = JSON.parse(strokeData);
      if (!parsed.strokes) return;
      const sx = canvas.width / 500;
      const sy = canvas.height / 150;
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      for (const stroke of parsed.strokes) {
        if (stroke.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(stroke[0].x * sx, stroke[0].y * sy);
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x * sx, stroke[i].y * sy);
        }
        ctx.stroke();
      }
    } catch {
      /* ignore */
    }
  }, [strokeData, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}

// ─── Field Input ───

export function FieldInput({
  field,
  value,
  onChange,
  readOnly,
}: {
  field: FormFieldDef;
  value: string;
  onChange: (val: string) => void;
  readOnly: boolean;
}) {
  const baseStyle: CSSProperties = {
    width: '100%',
    height: 36,
    padding: '0 10px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: readOnly ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
    fontSize: 'var(--font-size-sm)',
    color: readOnly ? 'var(--text-muted)' : 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  switch (field.type) {
    // PRD.322 — Rich Text.
    //
    // READ-ONLY: formatted definition wording, no editor chrome, not an answer.
    // EDITABLE: the stored instance value once one exists, otherwise the
    // definition default. `resolveEditableRichText` owns that rule, so a
    // patient's saved wording never starts tracking a later definition edit.
    case 'rich-text': {
      const cfg = (field.config as RichTextConfig | undefined) ?? {};
      if (!cfg.editableWhenFilling) {
        return (
          <RichTextView doc={parseRichText(cfg.content ?? '')} style={{ color: 'var(--text)' }} />
        );
      }
      const doc = resolveEditableRichText(value === '' ? '' : (value ?? null), cfg.content);
      if (readOnly) {
        return <RichTextView doc={doc} style={{ color: 'var(--text-muted)' }} />;
      }
      return (
        <RichTextEditor
          ariaLabel={field.label || 'Rich text'}
          value={stringifyRichText(doc)}
          onChange={onChange}
          minHeight={120}
        />
      );
    }
    case 'text':
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          style={baseStyle}
        />
      );
    case 'textarea':
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          rows={3}
          style={{
            ...baseStyle,
            height: 'auto',
            minHeight: 72,
            padding: '8px 10px',
            resize: 'vertical',
          }}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          style={baseStyle}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      );
    case 'select':
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          style={baseStyle}
        >
          <option value="">Select...</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case 'multi-select': {
      const selected = value
        ? value.startsWith('[')
          ? (JSON.parse(value) as string[])
          : [value]
        : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(field.options ?? []).map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: readOnly ? 'default' : 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((v) => v !== opt.value)
                      : [...selected, opt.value];
                    onChange(JSON.stringify(next));
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    case 'yes-no':
      return (
        <div style={{ display: 'flex', gap: 16 }}>
          {['yes', 'no'].map((opt) => (
            <label
              key={opt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
              }}
            >
              <input
                type="radio"
                name={field.key}
                value={opt}
                checked={value === opt}
                disabled={readOnly}
                onChange={(e) => onChange(e.target.value)}
                style={{ accentColor: 'var(--color-primary)' }}
              />
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </label>
          ))}
        </div>
      );
    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      );
  }
}

// ─── Section Renderer ───

export function SectionRenderer({
  section,
  values,
  signatures,
  onValueChange,
  onSignatureSave,
  readOnly,
  bindingData,
}: {
  section: FormSection;
  values: Record<string, string>;
  signatures?: Record<string, string>;
  onValueChange: (fieldKey: string, val: string) => void;
  onSignatureSave?: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
  bindingData: Record<string, string>;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f9fafb)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-1)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {section.title || 'Section'}
      </h3>
      {section.description && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {section.description}
        </p>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-4)',
          marginTop: 'var(--sp-3)',
        }}
      >
        {section.fields.map((field) => {
          const isFullWidth =
            field.layout === 'full' ||
            field.type === 'textarea' ||
            field.type === 'signature' ||
            field.type === 'drawing';
          return (
            <div
              key={field.key}
              style={{
                gridColumn: isFullWidth ? '1 / -1' : undefined,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
              }}
            >
              <label
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {field.label || field.key}
                {field.required && (
                  <span style={{ color: 'var(--color-danger-text, #dc2626)' }}> *</span>
                )}
              </label>

              {field.type === 'signature' && onSignatureSave ? (
                <SignaturePad
                  fieldKey={field.key}
                  existingData={signatures?.[field.key] ?? null}
                  onSave={onSignatureSave}
                  readOnly={readOnly}
                />
              ) : field.type === 'drawing' ? (
                <div
                  style={{
                    border: '2px dashed rgba(22,163,74,0.4)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--sp-4)',
                    textAlign: 'center',
                    color: '#16a34a',
                    fontSize: 'var(--font-size-sm)',
                    background: 'rgba(22,163,74,0.04)',
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Drawing area (coming soon)
                </div>
              ) : (
                <FieldInput
                  field={field}
                  value={resolveBoundFieldValue(field, values, bindingData) ?? ''}
                  onChange={(val) => onValueChange(field.key, val)}
                  readOnly={readOnly || (!!field.binding && !isFieldEditable(field))}
                />
              )}

              {field.helpText &&
                field.type !== 'text' &&
                field.type !== 'textarea' &&
                field.type !== 'number' && (
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-placeholder, #9ca3af)',
                    }}
                  >
                    {field.helpText}
                  </span>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Mode Switch ───

export function ModeSwitch({
  mode,
  onChange,
  primary,
}: {
  mode: 'form' | 'render';
  onChange: (m: 'form' | 'render') => void;
  primary: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {(['render', 'form'] as const).map((m) => {
        const active = mode === m;
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            style={{
              height: 30,
              padding: '0 12px',
              border: '1px solid var(--border)',
              background: active ? primary : 'var(--bg-surface)',
              color: active ? '#fff' : 'var(--text)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              borderRadius:
                m === 'render'
                  ? 'var(--radius-md) 0 0 var(--radius-md)'
                  : '0 var(--radius-md) var(--radius-md) 0',
              marginLeft: m === 'form' ? -1 : 0,
            }}
          >
            {m === 'render' ? 'Render' : 'Form'}
          </button>
        );
      })}
    </div>
  );
}

// ─── Template Overlay View (Render mode) ───

export function TemplateOverlayView({
  pages,
  mappings,
  fields,
  values,
  signatures,
  schema,
  bindingData,
  formName,
  onValueChange,
  onSignatureSave,
  readOnly,
}: {
  pages: FormTemplatePageRow[];
  mappings: FormTemplateMappingRow[];
  fields: FormFieldDef[];
  values: Record<string, string>;
  signatures: Record<string, string>;
  schema: FormFieldsSchemaV1;
  bindingData: Record<string, string>;
  /**
   * PRD.325 — current definition name, for a header that follows it.
   *
   * REQUIRED on purpose: an absent name resolves to empty text and the header
   * renders nothing at all, so a forgotten prop looks exactly like "no header
   * configured". Making it mandatory turns that into a compile error.
   */
  formName: string;
  onValueChange: (fieldKey: string, val: string) => void;
  onSignatureSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
}) {
  // Retained only to pick which template page owns the flow anchor. It is no
  // longer a navigation cursor: the document renders every page.
  const [selectedPageIdx] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [editingField, setEditingField] = useState<{ key: string; mappingId: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sigModalField, setSigModalField] = useState<string | null>(null);
  // ─── PRD.324 — Rich Text flow pagination ───
  //
  // A Rich Text mapping is a FLOW ANCHOR: it starts where it was placed and
  // grows with the patient's content, spilling onto generated blank A4 pages.
  // Those pages are computed per instance and NEVER written to
  // `form_template_pages` — two patients on the same form can legitimately have
  // different page counts.
  const pageBoxRef = useRef<HTMLDivElement>(null);
  const [pageBox, setPageBox] = useState({ width: 0, height: 0 });
  const [measuredBlocks, setMeasuredBlocks] = useState<FlowBlock[]>([]);

  /**
   * The flow anchor for the CURRENT template page.
   *
   * V1 allows one anchor per page: two anchors would each claim the space below
   * themselves and their pushed content would collide with no deterministic
   * resolution. `selectFlowAnchor` picks the topmost and reports the rest.
   */
  const templatePage = pages[Math.min(selectedPageIdx, Math.max(0, pages.length - 1))];
  const anchorMapping = useMemo(() => {
    if (!templatePage) return null;
    const candidates = mappings.filter(
      (m) => m.page_id === templatePage.id && m.field_type === 'rich-text',
    );
    return selectFlowAnchor(candidates).anchor;
  }, [mappings, templatePage]);

  const anchorField = useMemo(
    () => (anchorMapping ? fields.find((f) => f.key === anchorMapping.field_key) : undefined),
    [anchorMapping, fields],
  );

  /**
   * The document to flow.
   *
   * Reuses PRD.322's model and its snapshot rule — the patient's stored value
   * wins once one exists, so an admin editing the definition later cannot
   * silently re-word an already-filled form.
   */
  const anchorDoc = useMemo(() => {
    if (!anchorField) return null;
    const cfg = (anchorField.config as RichTextConfig | undefined) ?? {};
    return cfg.editableWhenFilling
      ? resolveEditableRichText(values[anchorField.key] ?? null, cfg.content)
      : parseRichText(cfg.content ?? '');
  }, [anchorField, values]);

  const flowLayout = useMemo(() => {
    if (!anchorMapping || !anchorDoc) return null;
    return layoutRichTextFlow({
      anchor: { id: anchorMapping.id, y_pct: anchorMapping.y_pct, h_pct: anchorMapping.h_pct },
      blocks: measuredBlocks,
      samePageMappings: mappings
        .filter((m) => m.page_id === anchorMapping.page_id)
        .map((m) => ({
          id: m.id,
          y_pct: m.y_pct,
          h_pct: m.h_pct,
          // PRD.325 — the header is template chrome bound to its page. Without
          // this it would satisfy the geometric downstream rule and get pushed
          // onto a continuation page, which is the repetition PRD.325 forbids.
          fixed: isFormHeaderMapping(m),
        })),
    });
  }, [anchorMapping, anchorDoc, measuredBlocks, mappings]);

  /**
   * The page list actually shown: the uploaded template pages, plus generated
   * continuation pages inserted directly after the anchor's page.
   *
   * Uploaded pages keep their identity and order — a continuation page is never
   * allowed to repurpose or overwrite the next uploaded page.
   */
  const displayPages = useMemo(() => {
    const list = pages.map((p) => ({ kind: 'template' as const, page: p, flowPageIndex: 0 }));
    if (!flowLayout || !anchorMapping || flowLayout.continuationPages === 0) return list;
    const anchorIdx = pages.findIndex((p) => p.id === anchorMapping.page_id);
    if (anchorIdx < 0) return list;
    const generated = Array.from({ length: flowLayout.continuationPages }, (_, i) => ({
      kind: 'continuation' as const,
      page: null,
      flowPageIndex: i + 1,
    }));
    return [...list.slice(0, anchorIdx + 1), ...generated, ...list.slice(anchorIdx + 1)];
  }, [pages, flowLayout, anchorMapping]);

  useEffect(() => {
    const el = pageBoxRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setPageBox((prev) =>
        prev.width === r.width && prev.height === r.height
          ? prev
          : { width: r.width, height: r.height },
      );
    };
    measure();
    // The template image loads asynchronously and the window can resize, so a
    // one-off read would paginate against a zero-height box.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });

  /**
   * Mappings drawn on the page currently shown.
   *
   * Fixed mappings stay on their own template page. Downstream mappings are
   * TRANSLATED by the flow — actually moved, never hidden behind z-index — so a
   * signature pushed onto a continuation page renders there with its y
   * rewritten and its x/width/height untouched.
   */

  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [selectedPageIdx]);

  // If no pages or images failed to load → show values summary
  if (pages.length === 0 || imageError) {
    return (
      <div style={{ padding: 'var(--sp-5)' }}>
        {pages.length > 0 && imageError && (
          <div
            style={{
              padding: 'var(--sp-3)',
              marginBottom: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-warning-bg, #fefce8)',
              border: '1px solid var(--color-warning-text, #a16207)',
              color: 'var(--color-warning-text, #a16207)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Template images are unavailable. Showing values summary instead.
          </div>
        )}
        <div
          style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
        >
          {schema.sections.map((section) => (
            <div
              key={section.id}
              style={{
                background: 'var(--bg-surface-2, #f9fafb)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--sp-5)',
              }}
            >
              <h3
                style={{
                  margin: '0 0 var(--sp-3)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {section.title || 'Section'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                {section.fields.map((field) => {
                  const val = resolveInstanceValue(field, values, signatures, bindingData);
                  const isSig = field.type === 'signature';
                  return (
                    <div
                      key={field.key}
                      style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text-muted)',
                          minWidth: 140,
                          flexShrink: 0,
                        }}
                      >
                        {field.label || field.key}
                      </span>
                      {isSig && signatures[field.key] ? (
                        <SignaturePreview
                          strokeData={signatures[field.key]}
                          width={200}
                          height={60}
                        />
                      ) : (
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                          {val}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // PRD.324 — PageNav removed from the patient-facing render: every generated
  // page is now stacked and reachable by scrolling, so Next/Previous is no
  // longer part of the document model. Builder page selection is separate and
  // untouched (Map Fields keeps its own `currentPageIdx`).

  return (
    <div
      className="render-document"
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        // The page gap. Sheets stay discrete — never merged into one
        // continuous white surface.
        gap: 'var(--sp-6)',
        alignItems: 'center',
      }}
    >
      {/* PRD.324 — measures each Rich Text block at the mapping's true rendered
          width. Offscreen and aria-hidden; the pagination rules themselves are
          pure and unit-tested in `layoutRichTextFlow`. */}
      {anchorDoc && anchorMapping && pageBox.width > 0 && (
        <RichTextFlowMeasurer
          doc={anchorDoc}
          widthPx={(anchorMapping.w_pct / 100) * pageBox.width}
          pageHeightPx={pageBox.height}
          onMeasured={setMeasuredBlocks}
        />
      )}

      {/* PRD.324 — the rendered DOCUMENT: every generated page as its own
          fixed A4 sheet, stacked vertically with a visible gap. Continuation
          content must be readable by scrolling, never gated behind
          Next/Previous — PageNav remains available for the builder, but the
          patient-facing render no longer depends on it. */}
      {displayPages.map((currentDisplay, renderPageIndex) => {
        const currentPage = currentDisplay.kind === 'template' ? currentDisplay.page : undefined;
        // Per-page mapping selection, previously a single component-level memo
        // bound to the one visible page.
        const pageMappings = currentPage
          ? mappings.filter((m) => {
              if (m.page_id !== currentPage.id) return false;
              // Drawn by FlowFragmentLayer as flowing content, not a fixed box.
              if (anchorMapping && m.id === anchorMapping.id) return false;
              // Drawn by RelocatedMapping at its translated position; drawing
              // it here too would double-render and overlap the flow.
              if (flowLayout?.downstream.some((d) => d.mappingId === m.id)) return false;
              return true;
            })
          : [];
        return (
          <Fragment
            key={
              currentDisplay.kind === 'template'
                ? currentDisplay.page.id
                : `continuation-${currentDisplay.flowPageIndex}`
            }
          >
            {currentDisplay?.kind === 'continuation' && (
              // A generated continuation page: a plain white sheet at the SAME
              // aspect ratio as the template page, so the percentage coordinate
              // system carries over unchanged. The uploaded template image is
              // deliberately not repeated or stretched onto it.
              <div
                className="render-page"
                data-page-index={renderPageIndex}
                data-page-kind="continuation"
                style={{
                  position: 'relative',
                  alignSelf: 'center',
                  width: '100%',
                  maxWidth: 900,
                  // Identical sheet geometry to a template page, so the percentage
                  // coordinate system carries across unchanged. Blank white — the
                  // uploaded background is deliberately never repeated here.
                  aspectRatio: `1 / ${A4_ASPECT}`,
                  background: '#fff',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
                }}
              >
                {flowLayout && anchorDoc && (
                  <FlowFragmentLayer
                    doc={anchorDoc}
                    fragment={flowLayout.fragments.find(
                      (f) => f.pageIndex === currentDisplay.flowPageIndex,
                    )}
                    anchor={anchorMapping}
                    measured={measuredBlocks}
                  />
                )}
                {flowLayout &&
                  flowLayout.downstream
                    .filter((d) => d.pageIndex === currentDisplay.flowPageIndex)
                    .map((d) => {
                      const m = mappings.find((mm) => mm.id === d.mappingId);
                      const field = m ? fields.find((f) => f.key === m.field_key) : undefined;
                      if (!m || !field) return null;
                      return (
                        <RelocatedMapping
                          key={m.id}
                          mapping={m}
                          yPct={d.yPct}
                          field={field}
                          values={values}
                          signatures={signatures}
                          bindingData={bindingData}
                        />
                      );
                    })}
              </div>
            )}

            {currentPage && (
              <div
                // One measurement source: all sheets share the same fixed geometry,
                // so measuring the first is measuring all of them.
                ref={renderPageIndex === 0 ? pageBoxRef : undefined}
                className="render-page"
                data-page-index={renderPageIndex}
                data-page-kind="template"
                style={{
                  position: 'relative',
                  alignSelf: 'center',
                  width: '100%',
                  maxWidth: 900,
                  // FIXED SHEET HEIGHT. Previously the page was `inline-block` sized
                  // by the uploaded image, so there was no boundary for content to
                  // break against and flowed text painted straight past the bottom
                  // edge. A page is now one A4 sheet whose height never depends on
                  // how much content it holds.
                  aspectRatio: `1 / ${A4_ASPECT}`,
                  background: '#fff',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
                }}
              >
                <style>{`@keyframes _form_spin { to { transform: rotate(360deg) } }`}</style>
                <img
                  src={currentPage.image_url}
                  alt={`Page ${currentPage.page_number}`}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    // `contain` keeps the uploaded artwork undistorted inside the
                    // sheet; the sheet's size is authoritative, not the image's.
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: 'var(--radius-md)',
                  }}
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    setImageLoading(false);
                    setImageError(true);
                  }}
                />
                {imageLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(240,240,240,0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-muted)',
                      fontSize: 'var(--font-size-sm)',
                      zIndex: 10,
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ animation: '_form_spin 1s linear infinite' }}
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Loading...
                  </div>
                )}
                {/* PRD.324 — the flow's first fragment and any downstream rows that
              still fit render over the template page itself. */}
                {flowLayout && anchorDoc && anchorMapping?.page_id === currentPage.id && (
                  <>
                    <FlowFragmentLayer
                      doc={anchorDoc}
                      fragment={flowLayout.fragments.find((f) => f.pageIndex === 0)}
                      anchor={anchorMapping}
                      measured={measuredBlocks}
                    />
                    {flowLayout.downstream
                      .filter((d) => d.pageIndex === 0)
                      .map((d) => {
                        const m = mappings.find((mm) => mm.id === d.mappingId);
                        const f = m ? fields.find((ff) => ff.key === m.field_key) : undefined;
                        if (!m || !f) return null;
                        return (
                          <RelocatedMapping
                            key={m.id}
                            mapping={m}
                            yPct={d.yPct}
                            field={f}
                            values={values}
                            signatures={signatures}
                            bindingData={bindingData}
                          />
                        );
                      })}
                  </>
                )}
                {pageMappings.map((m) => {
                  // PRD.325 — the Form Header is a special mapping with no
                  // backing field. It renders ONLY here, on the template page
                  // it was placed on, so a generated continuation sheet never
                  // repeats it.
                  if (isFormHeaderMapping(m)) {
                    return (
                      <div
                        key={m.id}
                        data-mapping-kind="form_header"
                        style={{
                          position: 'absolute',
                          left: `${m.x_pct}%`,
                          top: `${m.y_pct}%`,
                          width: `${m.w_pct}%`,
                          // Authoritative height — a header never flows.
                          height: `${m.h_pct}%`,
                        }}
                      >
                        <FormHeaderBox
                          config={readFormHeaderConfig(m.config)}
                          formName={formName}
                        />
                      </div>
                    );
                  }
                  const field = fields.find((f) => f.key === m.field_key);
                  if (!field) return null;
                  const isSignature = field.type === 'signature';
                  const isDrawing = field.type === 'drawing';
                  const isBoundReadOnly = !!field.binding && !isFieldEditable(field);
                  const isEditable = !readOnly && !isDrawing && !isBoundReadOnly;
                  const val = resolveInstanceValue(field, values, signatures, bindingData);
                  const isEditingThis =
                    editingField?.key === field.key && editingField?.mappingId === m.id;
                  // PRD.323 — one shared resolver for label + alignment, so this
                  // final render cannot drift from Map Fields or Preview.
                  const presentation = readMappingPresentation(m.config);

                  return (
                    <div
                      key={m.id}
                      style={{
                        position: 'absolute',
                        left: `${m.x_pct}%`,
                        top: `${m.y_pct}%`,
                        width: `${m.w_pct}%`,
                        height: `${m.h_pct}%`,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 11,
                        color: isDrawing ? '#16a34a' : isSignature ? '#dc2626' : '#111',
                        fontWeight: 500,
                        overflow: isEditingThis ? 'visible' : 'hidden',
                        padding: 2,
                        background: readOnly
                          ? 'transparent'
                          : isDrawing
                            ? 'rgba(22,163,74,0.08)'
                            : isSignature
                              ? 'rgba(220,38,38,0.06)'
                              : 'transparent',
                        border: readOnly
                          ? 'none'
                          : isDrawing
                            ? '2px dashed rgba(22,163,74,0.4)'
                            : isSignature
                              ? '2px dashed rgba(220,38,38,0.4)'
                              : isEditingThis
                                ? '2px solid var(--color-primary, #2563eb)'
                                : isEditable
                                  ? '1px dashed rgba(37,99,235,0.35)'
                                  : undefined,
                        borderRadius: isDrawing || isSignature || isEditable ? 4 : 1,
                        cursor: isEditable ? 'pointer' : 'default',
                        zIndex: isEditingThis ? 20 : undefined,
                      }}
                      onClick={() => {
                        if (!isEditable) return;
                        if (isSignature) {
                          setSigModalField(field.key);
                        } else {
                          setEditingField({ key: field.key, mappingId: m.id });
                          setEditValue(resolveBoundFieldValue(field, values, bindingData) ?? '');
                        }
                      }}
                    >
                      <MappedFieldBox presentation={presentation} fieldLabel={field.label}>
                        {isSignature && signatures[field.key] ? (
                          <SignaturePreview
                            strokeData={signatures[field.key]}
                            width={100}
                            height={40}
                          />
                        ) : (
                          val
                        )}
                      </MappedFieldBox>

                      {isEditable && !isEditingThis && (
                        <span
                          style={{
                            position: 'absolute',
                            right: -1,
                            top: -1,
                            width: 14,
                            height: 14,
                            borderRadius: '0 4px 0 4px',
                            background: 'rgba(37,99,235,0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          </svg>
                        </span>
                      )}

                      {isEditingThis && !isSignature && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: 4,
                            background: '#fff',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            padding: 'var(--sp-3)',
                            zIndex: 30,
                            minWidth: 220,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--sp-2)',
                          }}
                        >
                          <label
                            style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}
                          >
                            {field.label || field.key}
                          </label>
                          {field.type === 'textarea' ? (
                            <textarea
                              autoFocus
                              rows={3}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              style={{
                                width: '100%',
                                fontSize: 13,
                                padding: '6px 8px',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                              }}
                            />
                          ) : field.type === 'select' || field.type === 'multi-select' ? (
                            <select
                              autoFocus
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              style={{
                                width: '100%',
                                fontSize: 13,
                                padding: '6px 8px',
                                height: 32,
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                              }}
                            >
                              <option value="">— Select —</option>
                              {(field.options ?? []).map((opt) => (
                                <option key={opt.id} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : field.type === 'yes-no' ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                              {['yes', 'no'].map((opt) => (
                                <label
                                  key={opt}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 13,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={`pin_${field.key}`}
                                    value={opt}
                                    checked={editValue === opt}
                                    onChange={() => setEditValue(opt)}
                                    style={{ accentColor: 'var(--color-primary)' }}
                                  />
                                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <input
                              autoFocus
                              type={
                                field.type === 'number'
                                  ? 'number'
                                  : field.type === 'date'
                                    ? 'date'
                                    : 'text'
                              }
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              style={{
                                width: '100%',
                                fontSize: 13,
                                padding: '6px 8px',
                                height: 32,
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                                boxSizing: 'border-box',
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  onValueChange(field.key, editValue);
                                  setEditingField(null);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                            />
                          )}
                          <div
                            style={{
                              display: 'flex',
                              gap: 'var(--sp-2)',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <button
                              onClick={() => {
                                onValueChange(field.key, '');
                                setEditValue('');
                                setEditingField(null);
                              }}
                              style={{
                                height: 28,
                                padding: '0 10px',
                                fontSize: 12,
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--bg-surface)',
                                cursor: 'pointer',
                                color: 'var(--text-muted)',
                              }}
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => {
                                onValueChange(field.key, editValue);
                                setEditingField(null);
                              }}
                              style={{
                                height: 28,
                                padding: '0 10px',
                                fontSize: 12,
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--color-primary, #2563eb)',
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {editingField && (
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 15 }}
                    onClick={() => setEditingField(null)}
                  />
                )}
              </div>
            )}
          </Fragment>
        );
      })}

      {sigModalField && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setSigModalField(null)}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              background: '#fff',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-5)',
              width: 540,
              maxWidth: '90vw',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                }}
              >
                {fields.find((f) => f.key === sigModalField)?.label || 'Signature'}
              </h3>
              <button
                onClick={() => setSigModalField(null)}
                style={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  color: 'var(--text-muted)',
                }}
              >
                ×
              </button>
            </div>
            <SignaturePad
              fieldKey={sigModalField}
              existingData={signatures[sigModalField] ?? null}
              onSave={(fk, du, sd) => {
                onSignatureSave(fk, du, sd);
                setSigModalField(null);
              }}
              readOnly={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PRD.324 — flow rendering helpers ───

/**
 * One page's worth of the Rich Text flow.
 *
 * Draws only the blocks the pure layout assigned to this page, reusing
 * PRD.322's `RichTextView` — there is no second Rich Text parser or renderer,
 * so bold/italic/underline and both list types survive a page break exactly as
 * they render anywhere else.
 */
function FlowFragmentLayer({
  doc,
  fragment,
  anchor,
  measured,
}: {
  doc: RichTextDoc;
  fragment: FlowFragment | undefined;
  anchor: FormTemplateMappingRow | null;
  /** Measured heights, needed to window a partially-placed block. */
  measured?: FlowBlock[];
}) {
  if (!fragment || !anchor) return null;
  if (fragment.pieces.length === 0) return null;

  const presentation = readMappingPresentation(anchor.config);
  // PRD.323/324 — the Form Label belongs to the START of the flow only. A label
  // repeated at the top of every continuation page would read as a new section.
  const showLabel = presentation.showLabel && fragment.pageIndex === 0;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${anchor.x_pct}%`,
        top: `${fragment.startYPct}%`,
        width: `${anchor.w_pct}%`,
        // Bounded to the space this sheet actually has left. Pagination decides
        // WHICH blocks land here; this stops a measurement error from painting
        // the surplus past the sheet edge, which is what the bug looked like.
        // It is not a clip of the content — the overflowing blocks are on the
        // next sheet, not hidden.
        maxHeight: `${100 - PAGE_BOTTOM_MARGIN_PCT - fragment.startYPct}%`,
        fontSize: 11,
        color: '#111',
      }}
    >
      {showLabel && (
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          {resolveMappingLabelText(presentation, undefined)}
        </div>
      )}
      {fragment.pieces.map((piece, i) => {
        const block = doc.blocks[piece.blockIndex];
        if (!block) return null;

        // A WHOLE block — the normal case.
        if (piece.fromLine === undefined) {
          return <RichTextView key={i} doc={{ version: 1, blocks: [block] }} />;
        }

        // A PARTIAL block: this sheet shows a slice of one oversized paragraph
        // or list item. The full block is rendered and shifted up so only its
        // own line range shows through a window of exactly that height. Nothing
        // is truncated — the remaining lines appear on the next sheet, and the
        // windows together show the block exactly once. Same idea as a CSS
        // fragmentainer, and it keeps bold/italic/list markup intact rather
        // than slicing text into new blocks.
        const lines = measured?.find((b) => b.index === piece.blockIndex)?.lineHeightsPct ?? [];
        const offset = lines.slice(0, piece.fromLine).reduce((a, b) => a + b, 0);
        const height = lines.slice(piece.fromLine, piece.toLine).reduce((a, b) => a + b, 0);
        return (
          <div key={i} style={{ height: `${height}%`, overflow: 'hidden', position: 'relative' }}>
            <div style={{ position: 'relative', top: `-${offset}%` }}>
              <RichTextView doc={{ version: 1, blocks: [block] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A downstream mapping the flow pushed onto a continuation page.
 *
 * Only `y` is rewritten — `x`, width and height are the admin's, so a
 * side-by-side signature/date pair keeps its geometry exactly.
 */
function RelocatedMapping({
  mapping,
  yPct,
  field,
  values,
  signatures,
  bindingData,
}: {
  mapping: FormTemplateMappingRow;
  yPct: number;
  field: FormFieldDef;
  values: Record<string, string>;
  signatures: Record<string, string>;
  bindingData: Record<string, string>;
}) {
  const presentation = readMappingPresentation(mapping.config);
  const isSignature = field.type === 'signature';
  const val = resolveInstanceValue(field, values, signatures, bindingData);

  return (
    <div
      style={{
        position: 'absolute',
        left: `${mapping.x_pct}%`,
        top: `${yPct}%`,
        width: `${mapping.w_pct}%`,
        height: `${mapping.h_pct}%`,
        display: 'flex',
        alignItems: 'center',
        fontSize: 11,
        color: isSignature ? '#dc2626' : '#111',
        fontWeight: 500,
        overflow: 'hidden',
        padding: 2,
      }}
    >
      <MappedFieldBox presentation={presentation} fieldLabel={field.label}>
        {isSignature && signatures[field.key] ? (
          <SignaturePreview strokeData={signatures[field.key]} width={100} height={40} />
        ) : (
          val
        )}
      </MappedFieldBox>
    </div>
  );
}
